// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { TestableText } from '@components/general/TestableComponents';
import AvatarLogo from '@components/general/AvatarLogo';

/**
 * Small pill badge used for the workflow meta (release tag, environment, run status).
 * @param {{ children: React.ReactNode, className?: string, testId?: string }} props
 */
const MetaBadge = ({ children, className = '', testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-sm font-medium ${className}`}
  >
    {children}
  </span>
);

MetaBadge.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  testId: PropTypes.string,
};

/**
 * Workflow summary strip: avatar, workflow name + environment badge and
 * description.
 *
 * @param {Object} props
 * @param {import('./businessView.types').ProjectSummary} props.summary
 * @returns {JSX.Element}
 */
const BusinessViewProjectSummary = ({ summary }) => {
  const { name, description, avatarUrl, tags } = summary || {};

  return (
    <div
      data-testid="bv-workflow-summary"
      className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between border-b border-gray-300 pb-4 dark:border-gray-700"
    >
      {/* Left: avatar + text */}
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="shrink-0">
          <AvatarLogo src={avatarUrl} alt={name} size="xl" displayType="avatar" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <TestableText dataTestId="bv-workflow-name" variant="h3" className="truncate !normal-case">
              {name}
            </TestableText>
            {tags && (
							tags.map((tag) => (
								<MetaBadge
									key={tag}
									testId={`bv-workflow-tag-${tag}`}									
									className="border-blue-500 text-blue-500 uppercase bg-blue-500/10"
								>
									{tag}
								</MetaBadge>
							))
						)}
          </div>
          <TestableText dataTestId="bv-workflow-description" variant="small" className="mt-1 text-gray-600 dark:text-gray-300">
            {description}
          </TestableText>
        </div>
      </div>
    </div>
  );
};

BusinessViewProjectSummary.propTypes = {
  summary: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    avatarUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    runStatus: PropTypes.string,
  }).isRequired,
};

export default BusinessViewProjectSummary;
